import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import {
  MARQUEE_STATE,
  MARQUEE_STATE_OPTIONS,
  MARQUEE_TYPE,
  mergeMarqueeDetailsWithLanguages,
  stripMarqueeDetails,
} from './marqueeConstants';

const EMPTY_FORM = {
  state: MARQUEE_STATE.ON,
  sort: 1,
  details: [],
};

export default function MarqueeEditModal({ open, marqueeId, marqueeType, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(marqueeId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const langList = await adminApi.newsLanguages();
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        if (marqueeId) {
          const data = await adminApi.getMarquee(marqueeId);
          if (cancelled) {
            return;
          }
          setForm({
            state: data.state ?? MARQUEE_STATE.ON,
            sort: data.sort ?? 1,
            details: mergeMarqueeDetailsWithLanguages(langList, data.details),
          });
        } else {
          setForm({
            state: MARQUEE_STATE.ON,
            sort: 1,
            details: mergeMarqueeDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, marqueeId]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  function handleReset() {
    setError('');
    if (isEdit) {
      setForm((prev) => ({
        ...prev,
        details: prev.details.map((detail) => ({ ...detail, title: '' })),
      }));
      return;
    }
    setForm({
      state: MARQUEE_STATE.ON,
      sort: 1,
      details: mergeMarqueeDetailsWithLanguages(languages, []),
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const details = stripMarqueeDetails(form.details).filter((d) => d.title?.trim());
    if (!details.length) {
      setError('至少填寫一個語系的標題');
      return;
    }
    const payload = {
      state: Number(form.state),
      sort: Number(form.sort),
      marqueeType,
      details,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateMarquee({ id: marqueeId, ...payload });
      } else {
        await adminApi.createMarquee(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '修改' : '新增'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p className="table-empty">載入中…</p>
        ) : (
          <form className="modal-panel__body marquee-edit-form" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <div className="form-field">
              <span className="form-field__label">狀態</span>
              <div className="product-status-radios">
                {MARQUEE_STATE_OPTIONS.map((option) => (
                  <label key={option.value} className="product-status-radios__item">
                    <input
                      type="radio"
                      name="marqueeState"
                      checked={Number(form.state) === option.value}
                      onChange={() => setForm((p) => ({ ...p, state: option.value }))}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="marqueeSort">
                <span className="form-label-required">顯示順序</span>
              </label>
              <input
                id="marqueeSort"
                type="number"
                min="1"
                value={form.sort}
                onChange={(e) => setForm((p) => ({ ...p, sort: e.target.value }))}
                required
              />
            </div>

            {form.details.map((detail, index) => (
              <div key={detail.lang} className="form-field">
                <label htmlFor={`marquee-title-${detail.lang}`}>
                  <span className="form-label-required">
                    標題（{detail._label || detail.lang}）
                  </span>
                </label>
                <input
                  id={`marquee-title-${detail.lang}`}
                  type="text"
                  value={detail.title || ''}
                  onChange={(e) => updateDetail(index, 'title', e.target.value)}
                />
              </div>
            ))}

            <div className="modal-panel__footer admin-modal__footer--center">
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? '提交中…' : '提交'}
              </button>
              <button type="button" className="btn btn-warning" onClick={handleReset} disabled={submitting}>
                重置
              </button>
              <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
                關閉
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
